import { InviteLinkWithAnalyticsType } from '@/shared/api'
import { PublishingDurationEnum } from '@synapex/prisma/client'
import {
    AnalyticsTableRowTypeChannels,
    AnalyticsTableRowTypeSites,
} from './types'
import { SiteLinkWithAnalyticsType } from '@/shared/api/site-links-group/site-links-group.types'
import { ensureHttps } from '@synapex/shared/src/utils/urlUtils'

const transformSharedFields = ({
    budget,
    status,
    publisher,
    external_channel,
    external_channel_id,
    publishing_duration,
}: Pick<
    InviteLinkWithAnalyticsType,
    | 'budget'
    | 'status'
    | 'external_channel'
    | 'external_channel_id'
    | 'publishing_duration'
> & {
    publisher: null | Pick<
        NonNullable<InviteLinkWithAnalyticsType['publisher']>,
        'average_reach' | 'err' | 'link_deleted_at'
    >
}) => {
    const averageReachCoerced = publisher?.average_reach ?? 0

    const publishingDuration: PublishingDurationEnum =
        (publishing_duration!) || 'day'

    return {
        budget: budget || 0,
        status,
        averageReach: publisher?.average_reach ?? undefined,
        err: publisher?.err ?? undefined,
        publishingDuration,
        publishingDeletionDate: publisher?.link_deleted_at
            ? new Date(publisher.link_deleted_at)
            : undefined,
        CPMForcasted:
            averageReachCoerced > 0 ? budget / averageReachCoerced : 0,

        rknStatus: external_channel?.rkn_status ?? undefined,
        photoUrl: external_channel?.small_photo_data_url ?? undefined,
        externalChannelId: external_channel_id,
    }
}

export const transformLinksToTableFormatChannels = (
    inviteLinks: InviteLinkWithAnalyticsType[]
): AnalyticsTableRowTypeChannels[] => {
    const transformedInviteLinks = inviteLinks.map((link) => {
        const publisher = link.publisher
        // const analytics = link.analytics
        const publishedAt = link.published_at
            ? new Date(link.published_at)
            : undefined

        const now = new Date()

        const isPublished =
            !!link.published_at &&
            now.getTime() >= new Date(link.published_at).getTime()

        const sharedFields = transformSharedFields(link)

        return {
            ...sharedFields,
            workspace_type: 'channel' as const,
            link_id: link.invite_link_id,
            title: link.external_channel
                ? link.external_channel.telegram_channel_name
                : link.invite_link_name,
            name: link.invite_link_name,
            eye: {
                channelName:
                    link.publisher?.telegram_channel_username || undefined,
                messageId: link.publisher?.telegram_post_id || undefined,
            },
            link: link.invite_link,
            creationDate: new Date(link.created_at),
            publishedAt: publishedAt,
            isPublished: isPublished,
            subscribers: publisher?.telegram_channel_subscribers || 0,
            reach: publisher?.telegram_post_reach || 0,
            subscribed: link.total_subscribers || 0,
            subscribedPerDay: link.subscribed_count_per_day,
            CPM: link.cpm || 0,
            CTR: link.ctr ? link.ctr * 100 : 0,
            CPS: link.cps || 0,
            unsubscribed: link.unsubscribed_count,
            unsubscriptionPercentage:
                link.total_subscribers || 0
                    ? (link.unsubscribed_count /
                          (link.total_subscribers || 0)) *
                      100
                    : 0, //link.unsubscribed_percentage ? link.unsubscribed_percentage * 100 : 0,
            totalSubscribed: link.subscribed_count || 0, //link.total_subscribers - link.unsubscribed_count || 0,
            total_CPS: link.total_cps || 0, // analytics && analytics.length > 0 ? analytics[analytics.length - 1].total_cps || 0 : 0,
            publisher: link.publisher ?? undefined,
            rknStatus: link.external_channel?.rkn_status ?? undefined,
            externalChannelId: link.external_channel_id,
            activeCreative: link.active_creative ?? null,
            source: link,
        }
    })

    const sortedInviteLinks = transformedInviteLinks.sort(
        (a, b) =>
            new Date(b.creationDate).getTime() -
            new Date(a.creationDate).getTime()
    )

    return sortedInviteLinks
}

export const transformLinksToTableFormatSites = (
    inviteLinks: SiteLinkWithAnalyticsType[]
): AnalyticsTableRowTypeSites[] => {
    const transformedInviteLinks = inviteLinks.map(
        (link): AnalyticsTableRowTypeSites => {
            const publisher = link.publisher

            const publishedAt = link.published_at
                ? new Date(link.published_at)
                : undefined

            const now = new Date()

            const isPublished =
                !!link.published_at &&
                now.getTime() >= new Date(link.published_at).getTime()

            const postReach = publisher?.telegram_post_reach || 0

            const CInPercent =
                link.users && link.users > 0 && link.leads
                    ? (link.leads / link.users) * 100
                    : 0

            const CTRInPercent =
                (postReach || postReach > 0) && !!link.users
                    ? (link.users / (postReach || postReach)) * 100
                    : 0

            const CPM = postReach && postReach > 0 && link.budget / postReach

            const sharedFields = transformSharedFields(link)

            return {
                ...sharedFields,
                workspace_type: 'site',
                link_id: link.site_link_id,
                title: link.external_channel
                    ? link.external_channel.telegram_channel_name
                    : link.site_link_name,
                name: link.site_link_name,
                eye: {
                    siteName:
                        link.publisher?.telegram_channel_username || undefined,
                    messageId: link.publisher?.telegram_post_id || undefined,
                },
                link: ensureHttps(link?.utmLink?.baseUrl),
                creationDate: new Date(link.created_at),
                publishedAt: publishedAt,
                isPublished: isPublished,
                subscribers: publisher?.telegram_channel_subscribers || 0,
                reach: postReach || 0,
                CPV: link.cpv || 0,
                CPU: link.cpu || 0,
                CPL: link.cpl || 0,
                CPM: CPM || link.cpm || 0,
                publisher: link.publisher ?? undefined,
                CInPercent,
                CTRInPercent,
                leads: link.leads || 0,
                utmLink: link.utmLink,
                users: link.users || 0,
                rknStatus: link.external_channel?.rkn_status ?? undefined,
                activeCreative: link.active_creative,
                source: link,
            }
        }
    )

    const sortedInviteLinks = transformedInviteLinks.sort(
        (a, b) =>
            new Date(b.creationDate).getTime() -
            new Date(a.creationDate).getTime()
    )

    return sortedInviteLinks
}
